import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useAuth } from "../contexts/AuthContext"
import type { Comment, Photo, SpotDetail } from "../types"
import CameraUpload from "./CameraUpload"
import PhotoGallery from "./PhotoGallery"
import StarRating from "./StarRating"

const API_URL = import.meta.env.VITE_API_URL || "/api"

const CATEGORY_COLORS: Record<string, string> = {
  free_wild: "bg-forest/10 text-forest dark:bg-forest/20 dark:text-forest-light",
  paid_campsite: "bg-steel/10 text-steel-dark dark:bg-steel/20 dark:text-steel-light",
  rest_area: "bg-sand/20 text-amber-800 dark:bg-sand/20 dark:text-sand-light",
  service_point: "bg-deepTeal/10 text-deepTeal dark:bg-deepTeal/20 dark:text-deepTeal-light",
}

const CATEGORY_ICONS: Record<string, string> = {
  free_wild: "🏕️",
  paid_campsite: "🏡",
  rest_area: "🅿️",
  service_point: "🔧",
}

const AMENITY_ICONS: Record<string, string> = {
  water: "💧",
  electricity: "⚡",
  wc: "🚻",
  shower: "🚿",
  wifi: "📶",
  waste_disposal: "🗑️",
  pet_friendly: "🐕",
  bbq: "🔥",
  shade: "🌳",
  flat: "📐",
  quiet: "🔇",
}

interface SpotDetailProps {
  spot: SpotDetail
  onClose: () => void
  onSpotUpdated: (spot: SpotDetail) => void
  onDelete?: (spotId: string) => void
}

export default function SpotDetailSheet({ spot, onClose, onSpotUpdated, onDelete }: SpotDetailProps) {
  const { t } = useTranslation()
  const { auth } = useAuth()
  const [activePhoto, setActivePhoto] = useState<number | null>(null)
  const [commentText, setCommentText] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const categoryLabel = (cat: string) => t(`spot.categories.${cat}`)

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!commentText.trim() || !auth.token) return
    setError(null)
    setLoading(true)
    try {
      const res = await fetch(`${API_URL}/spots/${spot.id}/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify({ text: commentText.trim() }),
      })
      if (!res.ok) throw new Error("Failed to add comment")
      const comment: Comment = await res.json()
      onSpotUpdated({ ...spot, comments: [comment, ...spot.comments] })
      setCommentText("")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add comment")
    } finally {
      setLoading(false)
    }
  }

  const handleRate = async (score: number) => {
    if (!auth.token) return
    setError(null)
    try {
      const res = await fetch(`${API_URL}/spots/${spot.id}/ratings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify({ score }),
      })
      if (!res.ok) throw new Error("Failed to rate")
      const rating = await res.json()
      const newRatings = spot.ratings.some((r) => r.user_id === rating.user_id)
        ? spot.ratings.map((r) => (r.user_id === rating.user_id ? rating : r))
        : [...spot.ratings, rating]
      const avg =
        newRatings.length > 0
          ? Math.round((newRatings.reduce((a, b) => a + b.score, 0) / newRatings.length) * 10) / 10
          : null
      onSpotUpdated({
        ...spot,
        ratings: newRatings,
        rating_avg: avg,
        rating_count: newRatings.length,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to rate")
    }
  }

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}${window.location.pathname}?spot=${spot.id}`
    const shareData = {
      title: spot.name,
      text: spot.description || spot.name,
      url: shareUrl,
    }
    try {
      if (navigator.share) {
        await navigator.share(shareData)
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl)
        alert(t("spot.shareCopied"))
      } else {
        const textArea = document.createElement("textarea")
        textArea.value = shareUrl
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand("copy")
        document.body.removeChild(textArea)
        alert(t("spot.shareCopied"))
      }
    } catch {}
  }

  const handleToggleFavorite = async () => {
    if (!auth.token) return
    try {
      const res = await fetch(`${API_URL}/spots/${spot.id}/favorite`, {
        method: "POST",
        headers: { Authorization: `Bearer ${auth.token}` },
      })
      if (!res.ok) throw new Error("Failed")
      const { is_favorite } = await res.json()
      onSpotUpdated({ ...spot, is_favorite })
    } catch {}
  }

  const handlePhotoUploaded = (photo: Photo) => {
    onSpotUpdated({ ...spot, photos: [photo, ...spot.photos] })
  }

  const isOwner = auth.user?.id === spot.owner_id

  const handleDeletePhoto = async (photoId: string) => {
    if (!auth.token) return
    if (!confirm(t("spot.confirmDeletePhoto"))) return
    try {
      const res = await fetch(`${API_URL}/spots/${spot.id}/photos/${photoId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${auth.token}` },
      })
      if (!res.ok) throw new Error("Failed to delete photo")
      onSpotUpdated({ ...spot, photos: spot.photos.filter((p) => p.id !== photoId) })
    } catch {}
  }

  const handleDeleteComment = async (commentId: string) => {
    if (!auth.token) return
    if (!confirm(t("spot.confirmDeleteComment"))) return
    try {
      const res = await fetch(`${API_URL}/spots/${spot.id}/comments/${commentId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${auth.token}` },
      })
      if (!res.ok) throw new Error("Failed to delete comment")
      onSpotUpdated({ ...spot, comments: spot.comments.filter((c) => c.id !== commentId) })
    } catch {}
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 flex flex-col max-h-[85vh]">
      <div className="absolute inset-x-0 top-0 h-8 flex justify-center items-start pt-2 bg-white dark:bg-slate-900 rounded-t-2xl">
        <div className="w-12 h-1 rounded-full bg-gray-300 dark:bg-slate-600" />
      </div>

      <div className="flex-1 bg-white dark:bg-slate-900 rounded-t-2xl shadow-[0_-4px_20px_rgba(0,0,0,0.15)] overflow-y-auto pt-8 pb-24">
        <div className="px-5 space-y-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">
                {spot.name}
              </h2>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span
                  className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                    CATEGORY_COLORS[spot.category] || "bg-gray-100 text-gray-700"
                  }`}
                >
                  <span>{CATEGORY_ICONS[spot.category] || "📍"}</span>
                  {categoryLabel(spot.category)}
                </span>
                {spot.rating_count > 0 && (
                  <span className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                    <StarRating score={spot.rating_avg} size="sm" />
                    <span className="font-medium">{spot.rating_avg}</span>
                    <span className="text-xs">({spot.rating_count})</span>
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleShare}
                className="text-xl transition-transform active:scale-90 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 leading-none p-1"
                title={t("spot.share")}
              >
                🔗
              </button>
              {auth.isAuthenticated && (
                <button
                  onClick={handleToggleFavorite}
                  className={`text-2xl transition-transform active:scale-90 ${
                    spot.is_favorite ? "text-yellow-400" : "text-gray-300 dark:text-gray-600"
                  }`}
                >
                  {spot.is_favorite ? "★" : "☆"}
                </button>
              )}
              {onDelete && auth.user?.id === spot.owner_id && (
                <button
                  onClick={() => {
                    if (confirm(t("spot.confirmDelete"))) {
                      onDelete(spot.id)
                    }
                  }}
                  className="text-red-400 hover:text-red-600 text-xl leading-none p-1"
                  title={t("spot.delete")}
                >
                  🗑️
                </button>
              )}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl leading-none p-1"
              >
                &times;
              </button>
            </div>
          </div>

          {spot.amenities && spot.amenities.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {spot.amenities.map((a) => (
                <span
                  key={a}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 text-sm"
                >
                  <span>{AMENITY_ICONS[a] || ""}</span>
                  {t(`amenity.${a}`)}
                </span>
              ))}
            </div>
          )}

          {(spot.price_per_night !== null || spot.max_stay_hours !== null) && (
            <div className="flex gap-4 text-sm">
              {spot.price_per_night !== null && (
                <div className="bg-gray-50 dark:bg-slate-800 rounded-lg px-3 py-2">
                  <span className="text-gray-500 dark:text-gray-400">{t("spot.price")}</span>
                  <span className="font-semibold text-gray-900 dark:text-white ml-1">
                    {spot.price_per_night === 0 ? t("spot.free") : `€${spot.price_per_night}`}
                  </span>
                </div>
              )}
              {spot.max_stay_hours !== null && (
                <div className="bg-gray-50 dark:bg-slate-800 rounded-lg px-3 py-2">
                  <span className="text-gray-500 dark:text-gray-400">{t("spot.maxStay")}</span>
                  <span className="font-semibold text-gray-900 dark:text-white ml-1">
                    {spot.max_stay_hours}h
                  </span>
                </div>
              )}
            </div>
          )}

          {spot.description && (
            <p className="text-gray-700 dark:text-gray-300 text-base leading-relaxed">
              {spot.description}
            </p>
          )}

          <div className="text-sm text-gray-400 dark:text-gray-500">
            {spot.latitude.toFixed(5)}, {spot.longitude.toFixed(5)}
          </div>

          {spot.photos.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3 text-lg">
                {t("spot.photos")} ({spot.photos.length})
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {spot.photos.map((photo, idx) => (
                  <div key={photo.id} className="relative aspect-square rounded-xl overflow-hidden bg-gray-100 dark:bg-slate-800">
                    <button
                      onClick={() => setActivePhoto(idx)}
                      className="w-full h-full"
                    >
                      <img
                        src={photo.url}
                        alt={photo.caption || ""}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none"
                        }}
                      />
                    </button>
                    {isOwner && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeletePhoto(photo.id)
                        }}
                        className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/50 text-white text-xs flex items-center justify-center hover:bg-red-600 transition-colors"
                        title={t("spot.deletePhoto")}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {auth.isAuthenticated && <CameraUpload spotId={spot.id} onUploaded={handlePhotoUploaded} />}

          {auth.isAuthenticated && (
            <div className="bg-gray-50 dark:bg-slate-800 rounded-xl p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm">
                {t("spot.rateThis")}
              </h3>
              <StarRating score={null} interactive onRate={handleRate} size="lg" />
            </div>
          )}

          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3 text-lg">
              {t("spot.comments")} ({spot.comments.length})
            </h3>
            <div className="space-y-3">
              {spot.comments.map((comment) => (
                <div
                  key={comment.id}
                  className="bg-gray-50 dark:bg-slate-800 rounded-xl p-4"
                >
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-steel text-white flex items-center justify-center text-xs font-bold">
                        {(comment.username || "A").charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <span className="font-medium text-sm text-gray-900 dark:text-white">
                          {comment.username || t("spot.anonymous")}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">
                          {new Date(comment.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    {isOwner && (
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        className="text-gray-400 hover:text-red-500 text-sm px-2"
                        title={t("spot.deleteComment")}
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                    {comment.text}
                  </p>
                </div>
              ))}
              {spot.comments.length === 0 && (
                <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                  {t("spot.noComments")}
                </p>
              )}
            </div>
          </div>

          {auth.isAuthenticated && (
            <form onSubmit={handleAddComment} className="space-y-2">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={t("spot.commentPlaceholder")}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-base focus:outline-none focus:ring-2 focus:ring-steel"
                required
              />
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-xl bg-steel text-white font-medium hover:bg-steel-dark disabled:opacity-50 transition-colors"
              >
                {loading ? t("spot.sending") : t("spot.addComment")}
              </button>
            </form>
          )}
        </div>
      </div>

      {activePhoto !== null && (
        <PhotoGallery
          photos={spot.photos}
          startIndex={activePhoto}
          onClose={() => setActivePhoto(null)}
        />
      )}
    </div>
  )
}
